// Зона объекта: где он стоит, насколько широко считается «на месте» и как часто ждут обход.
//
// ## Почему поле ввода одно, а не «широта» и «долгота»
//
// Потому что их не знает наизусть никто. Человек открывает карту, находит въезд на базу и
// копирует оттуда — ссылкой или парой чисел. Два поля заставляют его разбирать скопированное
// руками, а перепутанные местами 61 и 73 увозят объект из Сургута в Карское море, причём на
// экране остаются два правдоподобных числа.
//
// Разбирает вставленное СЕРВЕР (`server/app/domain/geo/place.py`), а не эта форма. Правило одно
// на кабинет, приложение и перенос данных: второй разбор здесь разошёлся бы с первым на первом
// же новом виде ссылки, и разошёлся бы молча.
//
// ## Почему рядом карта
//
// Рамка правдоподобия ловит не все перепутанные координаты: Пермь наоборот выглядит как
// нормальная точка. Проверить остаётся только глазами, и на это должно уходить одно движение.
// Карта же — единственный способ задать территорию непростой формы: круг вокруг точки годится
// посту в здании и не годится базе с забором буквой Г.
//
// ## Радиус и область не показываются вместе
//
// Заданная область ОТМЕНЯЕТ круг — так считает и сервер, и телефон. Показывать оба значило бы
// обещать зону, которой не существует, и спорить о ней потом с человеком, который её задал.

import { RiMapPin2Line } from '@remixicon/react';

import type { SiteOut } from '@/shared/api/endpoints/org';
import { SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { КартаОбъекта, type ТочкаКарты } from './КартаОбъекта';

/**
 * Радиусы словами человека, а не числом в метрах.
 *
 * «Насколько далеко от точки человек ещё на месте» — вопрос, на который никто не отвечает
 * числом. Отвечают предметом: пост в здании, огороженный двор, промплощадка, вахтовый посёлок.
 * 200 стоит умолчанием в базе с первой миграции, и менять его здесь незачем.
 */
export const РАДИУСЫ: readonly { readonly метры: number; readonly подпись: string }[] = [
  { метры: 100, подпись: '100 м — вход в здание, точный пост' },
  { метры: 200, подпись: '200 м — двор, огороженная территория' },
  { метры: 500, подпись: '500 м — база, склад, промплощадка' },
  { метры: 1000, подпись: '1 км — вахтовый посёлок, карьер' },
];

export const РАДИУС_ПО_УМОЛЧАНИЮ = 200;

/** Как часто ждут обход. Тоже предметом: «раз в смену» понятнее, чем «12». */
export const ИНТЕРВАЛЫ_ОБХОДА: readonly { readonly часы: number; readonly подпись: string }[] = [
  { часы: 2, подпись: 'каждые 2 часа — усиленная охрана' },
  { часы: 4, подпись: 'каждые 4 часа — обычный режим' },
  { часы: 6, подпись: 'каждые 6 часов' },
  { часы: 12, подпись: 'дважды за сутки' },
  { часы: 24, подпись: 'раз в сутки' },
];

/** «Обходов здесь не ждут» — законный ответ, а не незаполненное поле. */
export const БЕЗ_ОБХОДОВ = 'нет';

/** Точка объекта одной строкой — ровно в том виде, в каком её примут обратно. */
export function местоСтрокой(объект: { lat: string | null; lon: string | null }): string {
  if (объект.lat === null || объект.lon === null) return '';
  return `${объект.lat}, ${объект.lon}`;
}

/**
 * Ссылка на точку в «Яндекс Картах».
 *
 * ⚠️ В адресе карты порядок ОБРАТНЫЙ: сначала долгота, потом широта. Перепутать значит увезти
 * человека на другой конец страны ровно в тот момент, когда он проверяет, что не перепутал сам.
 */
export function ссылкаНаКарту(lat: string, lon: string): string {
  return `https://yandex.ru/maps/?pt=${lon},${lat}&z=17&l=map`;
}

/** Варианты радиуса вместе с уже записанным: чужое значение из переноса данных не теряется. */
function вариантыРадиуса(текущий: number): { value: string; label: string }[] {
  const свои = РАДИУСЫ.map((строка) => ({
    value: String(строка.метры),
    label: строка.подпись,
  }));
  if (РАДИУСЫ.some((строка) => строка.метры === текущий)) return свои;
  // Записанное когда-то значение обязано остаться видимым: иначе поле показало бы пустоту, а
  // первое же сохранение карточки молча заменило бы радиус на первый пункт списка.
  return [{ value: String(текущий), label: `${текущий} м — задано ранее` }, ...свои];
}

function вариантыОбхода(текущий: number | null): { value: string; label: string }[] {
  const свои = ИНТЕРВАЛЫ_ОБХОДА.map((строка) => ({
    value: String(строка.часы),
    label: строка.подпись,
  }));
  if (текущий === null || ИНТЕРВАЛЫ_ОБХОДА.some((с) => с.часы === текущий)) return свои;
  return [{ value: String(текущий), label: `каждые ${текущий} ч — задано ранее` }, ...свои];
}

/** Разбор пары «широта, долгота» ДЛЯ КАРТЫ. Ссылки не разбираем: это делает сервер. */
function точкаИзПоля(значение: string): ТочкаКарты | null {
  const пара = /^\s*(-?\d{1,3}[.,]\d+)\s*[,;\s]\s*(-?\d{1,3}[.,]\d+)\s*$/.exec(значение);
  if (пара === null || пара[1] === undefined || пара[2] === undefined) return null;
  return { lat: пара[1].replace(',', '.'), lon: пара[2].replace(',', '.') };
}

export interface ЗонаНаКартеProps {
  место: string;
  радиус: number;
  область: ТочкаКарты[] | null;
  обход: number | null;
  наМесто: (значение: string) => void;
  наРадиус: (значение: number) => void;
  наОбласть: (значение: ТочкаКарты[] | null) => void;
  наОбход: (значение: number | null) => void;
  /** Что сервер сказал про вставленное место. Показывается под полем, а не общим тостом. */
  ошибка?: string;
  /** Приставка к идентификаторам полей: на экране одновременно бывает две формы. */
  префикс: string;
}

export function ЗонаНаКарте({
  место,
  радиус,
  область,
  обход,
  наМесто,
  наРадиус,
  наОбласть,
  наОбход,
  ошибка,
  префикс,
}: ЗонаНаКартеProps) {
  const точка = точкаИзПоля(место);
  const естьОбласть = (область ?? []).length >= 3;

  return (
    <>
      <КартаОбъекта
        точка={точка}
        область={область}
        радиусМетров={радиус}
        наТочку={(новая) => наМесто(`${новая.lat}, ${новая.lon}`)}
        наОбласть={наОбласть}
      />

      <TextField
        id={`${префикс}-map-point`}
        label='Где объект на карте'
        icon={RiMapPin2Line}
        value={место}
        onChange={наМесто}
        placeholder='61.254012, 73.396215 или ссылка с карты'
        maxLength={500}
        error={ошибка}
        hint={
          <>
            Можно нажать по карте, а можно вставить сюда скопированное: пару чисел либо ссылку из
            «Яндекс Карт», 2ГИС или Google. Пусто — зоны у объекта нет, и отметку на посту принимаем
            без сверки места.
          </>
        }
      />

      {estьОбластьПодпись(естьОбласть) ? (
        <p className='text-paragraph-xs text-text-sub-600'>
          Территория обведена на карте — она и есть зона объекта. Круг вокруг точки при этом не
          используется: два ответа на один вопрос никому не нужны.
        </p>
      ) : (
        <SelectField
          id={`${префикс}-geofence`}
          label='Зона вокруг точки'
          value={String(радиус)}
          onChange={(значение) => наРадиус(Number(значение))}
          options={вариантыРадиуса(радиус)}
          placeholder='Выберите ширину зоны'
          hint={
            место.trim() !== ''
              ? 'Телефон сам отметит вход в зону и выход из неё, даже без связи.'
              : 'Зона заработает, когда появится точка: сейчас сверять место не с чем.'
          }
        />
      )}

      <SelectField
        id={`${префикс}-patrol`}
        label='Обход ждут не реже чем'
        value={обход === null ? БЕЗ_ОБХОДОВ : String(обход)}
        onChange={(значение) => наОбход(значение === БЕЗ_ОБХОДОВ ? null : Number(значение))}
        options={вариантыОбхода(обход)}
        noneLabel='обходов здесь не ждут'
        placeholder='Выберите, как часто'
        hint='Если обхода нет дольше срока, старший объекта получит уведомление. Это повод позвонить, а не нарушение: телефон садится, метку заклеивают, человека уводят на другой пост.'
      />
    </>
  );
}

/** Отдельной функцией, чтобы условие в разметке читалось словом, а не длиной списка. */
function estьОбластьПодпись(естьОбласть: boolean): boolean {
  return естьОбласть;
}

/** Точка и радиус объекта в виде, готовом для карточки. */
export function зонаОбъекта(site: SiteOut): {
  точка: string | null;
  ссылка: string | null;
  радиус: string;
} {
  const точка = местоСтрокой(site);
  return {
    точка: точка === '' ? null : точка,
    ссылка: site.lat === null || site.lon === null ? null : ссылкаНаКарту(site.lat, site.lon),
    радиус:
      (site.geofence_polygon ?? []).length >= 3
        ? 'территория обведена на карте'
        : `${site.geofence_radius_m} м вокруг точки`,
  };
}
